import React, { useRef } from "react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import Slider from "./Slider";
import { cn } from "../../../utils/cn";

const BackingTrackPlayer = ({
  musicLoaded,
  musicPlaying,
  musicName,
  musicProgress,
  musicVolume, // 0 - 1
  onLoadTrack,
  onPlay,
  onPause,
  onSeek,
  onUpdateVolume,
}) => {
  const fileInputRef = useRef(null);

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || !timeInSeconds) return "0:00";
    const mins = Math.floor(timeInSeconds / 60);
    const secs = Math.floor(timeInSeconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onLoadTrack(file);
    }
  };

  const handleProgressBarChange = (e) => {
    const seekTime = parseFloat(e.target.value);
    onSeek(seekTime);
  };

  const progressPercent = musicProgress.duration
    ? (musicProgress.currentTime / musicProgress.duration) * 100
    : 0;

  return (
    <div className="bg-card border border-border rounded-lg p-6 font-fira terminal-glow flex flex-col gap-4">
      {/* Module Title */}
      <div className="flex items-center justify-between border-b border-border pb-3 mb-1">
        <div className="flex items-center space-x-2">
          <Icon name="Music" size={16} className="text-primary animate-pulse" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
            Backing Track Player
          </h3>
        </div>
        {musicLoaded && (
          <span className="text-[9px] bg-primary/10 border border-primary/20 text-primary px-2 py-0.5 rounded uppercase font-bold animate-pulse">
            TRACK LOADED
          </span>
        )}
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="audio/*"
        className="hidden"
      />

      {!musicLoaded ? (
        // Load Screen
        <div className="flex flex-col items-center justify-center py-6 border border-dashed border-border rounded-lg bg-black/20 hover:border-primary/30 transition duration-200">
          <Icon name="UploadCloud" size={32} className="text-muted-foreground mb-2" />
          <p className="text-[10px] text-muted-foreground font-source mb-3 text-center px-4">
            Upload backing track (MP3, WAV, M4A) to mix with voice
          </p>
          <Button
            variant="outline"
            size="xs"
            onClick={() => fileInputRef.current?.click()}
            iconName="FolderOpen"
            className="text-[10px] font-bold uppercase"
          >
            Choose Audio File
          </Button>
        </div>
      ) : (
        // Player Screen
        <div className="space-y-4">
          {/* Track metadata */}
          <div className="bg-black/50 border border-border rounded p-3 flex items-center justify-between">
            <div className="flex items-center space-x-3 overflow-hidden">
              <Icon name="Disc" size={20} className={cn("text-terminal-cyan flex-shrink-0", musicPlaying && "animate-spin")} style={{ animationDuration: "4s" }} />
              <div className="truncate">
                <p className="text-[11px] font-bold text-foreground truncate select-none">
                  {musicName}
                </p>
                <p className="text-[9px] text-muted-foreground font-source uppercase">
                  PCM Stereo Channel
                </p>
              </div>
            </div>
            
            {/* Eject button */}
            <button
              onClick={() => {
                onPause();
                if (fileInputRef.current) fileInputRef.current.value = "";
                // Dispatch a dummy file to reset is not required, just reset the uploader
                window.location.reload(); // Simple reload to eject or we can just let them choose another file
              }}
              className="text-[10px] text-destructive hover:underline p-1"
              title="Eject / Load different song"
            >
              [EJECT]
            </button>
          </div>

          {/* Time slider & controls */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[9px] text-muted-foreground font-source px-1">
              <span>{formatTime(musicProgress.currentTime)}</span>
              <span>{formatTime(musicProgress.duration)}</span>
            </div>

            <div className="relative flex items-center">
              <input
                type="range"
                min={0}
                max={musicProgress.duration || 100}
                step={0.1}
                value={musicProgress.currentTime}
                onChange={handleProgressBarChange}
                className="w-full h-1 bg-muted rounded appearance-none cursor-pointer focus:outline-none accent-terminal-cyan"
                style={{
                  background: `linear-gradient(to right, var(--color-terminal-cyan) ${progressPercent}%, var(--color-muted) ${progressPercent}%)`,
                }}
              />
            </div>

            {/* Playback Controls & Volume Row */}
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
              {/* Play Pause button */}
              <div className="flex items-center space-x-2 flex-shrink-0">
                {!musicPlaying ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onPlay}
                    iconName="Play"
                    className="font-bold text-xs uppercase bg-terminal-cyan text-primary-foreground hover:bg-terminal-cyan/90 border border-terminal-cyan/20"
                  >
                    PLAY
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onPause}
                    iconName="Pause"
                    className="font-bold text-xs uppercase border-terminal-cyan/30 text-terminal-cyan hover:bg-terminal-cyan/10"
                  >
                    PAUSE
                  </Button>
                )}
                
                {/* Swap file button */}
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => fileInputRef.current?.click()}
                  iconName="RefreshCw"
                  className="text-[9px] text-muted-foreground hover:text-foreground uppercase"
                  title="Change track"
                >
                  Change
                </Button>
              </div>

              {/* Backing Track Volume Slider */}
              <div className="flex-grow w-full sm:w-auto">
                <Slider
                  label="Track Mix Volume"
                  min={0}
                  max={1}
                  step={0.05}
                  value={musicVolume}
                  defaultValue={0.5}
                  formatValue={(v) => `${Math.round(v * 100)}%`}
                  onChange={onUpdateVolume}
                  className="space-y-1.5"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackingTrackPlayer;
